import { useGetTicketTypesMutation } from '@services/member/list/list.endpoints';
import { TicketType } from '@services/member/list/ticket-types/ticket-types.types';
import { useGetMemberMutation } from '@services/member/member/member.endpoints';
import { Member } from '@services/member/member/member.types';
import {
    useGetTicketingMutation,
    useCreateTicketingMutation,
    useUpdateTicketingMutation
} from '@services/member/ticketing/ticketing.endpoints';
import { TicketingToCreate } from '@services/member/ticketing/ticketing.types';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

export const useAddPageVM = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const memberId = searchParams.get('memberId');
    const [ticketing, setTicketing] = useState<TicketingToCreate>();
    const [ticketTypes, setTicketTypes] = useState<TicketType[]>([]);
    const [member, setMember] = useState<Member>();

    const [getTicketTypes, { isSuccess: isGetTicketTypesSuccess, isLoading: isGetTicketTypesLoading, data: ticketTypesData }] =
        useGetTicketTypesMutation();

    const [
        getMember,
        {
            isLoading: isGetMemberLoading,
            isError: isGetMemberError,
            isSuccess: isGetMemberSuccess,
            error: getMemberError,
            data: getMemberResponse
        }
    ] = useGetMemberMutation();

    const [
        getTicketing,
        {
            isLoading: isGetTicketingLoading,
            isError: isGetTicketingError,
            error: getTicketingError,
            isSuccess: isGetTicketingSuccess,
            data: getTicketingResponse
        }
    ] = useGetTicketingMutation();

    const [
        createTicketing,
        {
            isLoading: isCreateTicketingLoading,
            isError: isCreateTicketingError,
            isSuccess: isCreateTicketingSuccess,
            error: createTicketingError,
            data: createTicketingResponse
        }
    ] = useCreateTicketingMutation();

    const [
        updateTicketing,
        {
            isLoading: isUpdateTicketingLoading,
            isError: isUpdateTicketingError,
            error: updateTicketingError,
            isSuccess: isUpdateTicketingSuccess,
            data: updateTicketingResponse
        }
    ] = useUpdateTicketingMutation();

    useEffect(() => {
        if (!id) return;
        getTicketing({ id });
    }, [id, getTicketing]);

    useEffect(() => {
        if (!memberId) return;
        getMember({ id: memberId });
    }, [memberId]);

    useEffect(() => {
        getTicketTypes(undefined);
    }, [getTicketTypes]);

    useEffect(() => {
        if (isGetTicketTypesLoading) return;
        if (isGetTicketTypesSuccess && ticketTypesData) {
            setTicketTypes(ticketTypesData.data);
        }
    }, [isGetTicketTypesSuccess, isGetTicketTypesLoading, ticketTypesData]);

    useEffect(() => {
        if (isGetMemberLoading) return;
        if (isGetMemberError && getMemberError) return;
        if (isGetMemberSuccess && getMemberResponse) {
            setMember(getMemberResponse.data);
        }
    }, [isGetMemberError, isGetMemberSuccess, getMemberError, getMemberResponse, isGetMemberLoading]);

    useEffect(() => {
        if (isUpdateTicketingLoading) return;
        if (isUpdateTicketingError) return;
        if (updateTicketingError) return;

        if (isUpdateTicketingSuccess && updateTicketingResponse) {
            navigate(`/members-management/ticketings`);
        }
    }, [
        isUpdateTicketingError,
        isUpdateTicketingSuccess,
        updateTicketingError,
        updateTicketingResponse,
        isUpdateTicketingLoading,
        navigate
    ]);

    useEffect(() => {
        if (isCreateTicketingLoading) return;
        if (isCreateTicketingError) return;
        if (createTicketingError) return;

        if (isCreateTicketingSuccess && createTicketingResponse) {
            if (memberId) {
                navigate(`/members-management/members/${memberId}/details/tickets`);
            } else {
                navigate(`/members-management/ticketings`);
            }
        }
    }, [
        isCreateTicketingError,
        isCreateTicketingSuccess,
        createTicketingError,
        createTicketingResponse,
        isCreateTicketingLoading,
        navigate
    ]);

    useEffect(() => {
        if (isGetTicketingLoading) return;
        if (isGetTicketingError) return;
        if (getTicketingError) return;

        if (isGetTicketingSuccess && getTicketingResponse) {
            setTicketing(getTicketingResponse.data);
        }
    }, [isGetTicketingError, isGetTicketingSuccess, getTicketingError, getTicketingResponse, isGetTicketingLoading]);

    const submitForm = async (values: TicketingToCreate) => {
        if (ticketing && id) {
            await updateTicketing({ id: id, ticketing: values });
        } else {
            await createTicketing(values);
        }
    };

    return {
        ticketing,
        isEdit: !!id,
        submitForm,
        isLoading: isCreateTicketingLoading || isGetTicketingLoading || isUpdateTicketingLoading,
        ticketTypes,
        member
    };
};
